// Define action types as string literals for better type safety
type ActionTypes =
  | "GENERATE_NONCE"
  | "AUTH_LOGIN"
  | "AUTH_LOGOUT"
  | "AUTH_LOGIN_ERROR"
  | "ADD_NFT"
  | "CATEGORY_LIST"
  | "COLLECTION_LIST"
  | "FETCHED_USER_PROFILE"
  | "API_FAILED"
  | "UPDATE_NFT";

// Define interfaces for each action
interface Action<T extends ActionTypes, D> {
  type: T;
  data: D;
}

// Define specific action interfaces
type GenerateNonceAction = Action<"GENERATE_NONCE", string>;
type AuthLoginAction = Action<"AUTH_LOGIN", AuthData | null>;
type AuthLogoutAction = Action<"AUTH_LOGOUT", null>;
type AuthLoginErrorAction = Action<"AUTH_LOGIN_ERROR", ErrorData>;
type AddNFTAction = Action<"ADD_NFT", NFTData>;
type CategoryListAction = Action<"CATEGORY_LIST", Category[]>;
type CollectionListAction = Action<"COLLECTION_LIST", Collection[]>;
type FetchedUserProfileAction = Action<"FETCHED_USER_PROFILE", UserProfile>;
type APIFailedAction = Action<"API_FAILED", ErrorData>;
type UpdateNFTAction = Action<"UPDATE_NFT", NFTData>;

// Define data type interfaces
interface AuthData {
  token?: string;
  user?: {
    id: string;
    username: string;
    email: string;
  };
  // Add other auth-related properties as needed
}

interface ErrorData {
  message: string;
  code?: number;
  // Add other error-related properties as needed
}

interface NFTData {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  // Add other NFT-related properties as needed
}

interface Category {
  id: string;
  name: string;
  // Add other category-related properties as needed
}

interface Collection {
  id: string;
  name: string;
  description?: string;
  // Add other collection-related properties as needed
}

interface UserProfile {
  id: string;
  username: string;
  email: string;
  bio?: string;
  avatarUrl?: string;
  // Add other user profile-related properties as needed
}

// Define union type for all actions
type ReduxAction =
  | GenerateNonceAction
  | AuthLoginAction
  | AuthLogoutAction
  | AuthLoginErrorAction
  | AddNFTAction
  | CategoryListAction
  | CollectionListAction
  | FetchedUserProfileAction
  | APIFailedAction
  | UpdateNFTAction;

// Reducer functions with proper typing
export function fetchNonce(
  state: string | null = null,
  action: ReduxAction
): string | null {
  switch (action.type) {
    case "GENERATE_NONCE":
      return action.data;
    default:
      return state;
  }
}

export function fetchAuthData(
  state: AuthData | null = null,
  action: ReduxAction
): AuthData | null {
  switch (action.type) {
    case "AUTH_LOGIN":
      return action.data;
    case "AUTH_LOGOUT":
    case "AUTH_LOGIN_ERROR":
      return null;
    default:
      return state;
  }
}

export function fetchNewNFTId(
  state: NFTData | null = null,
  action: ReduxAction
): NFTData | null {
  switch (action.type) {
    case "ADD_NFT":
      return action.data;
    default:
      return state;
  }
}

export function fetchCategoryList(
  state: Category[] | null = null,
  action: ReduxAction
): Category[] | null {
  switch (action.type) {
    case "CATEGORY_LIST":
      return action.data;
    default:
      return state;
  }
}

export function fetchCollectionList(
  state: Collection[] | null = null,
  action: ReduxAction
): Collection[] | null {
  switch (action.type) {
    case "COLLECTION_LIST":
      return action.data;
    default:
      return state;
  }
}

export function fetchUserProfile(
  state: UserProfile | null = null,
  action: ReduxAction
): UserProfile | null {
  switch (action.type) {
    case "FETCHED_USER_PROFILE":
      return action.data;
    default:
      return state;
  }
}

export function fetchResponseFailed(
  state: ErrorData | null = null,
  action: ReduxAction
): ErrorData | null {
  switch (action.type) {
    case "API_FAILED":
      return action.data;
    default:
      return state;
  }
}

export function fetchUpdatedNFTId(
  state: NFTData | null = null,
  action: ReduxAction
): NFTData | null {
  switch (action.type) {
    case "UPDATE_NFT":
      return action.data;
    default:
      return state;
  }
}
